import { gql, useMutation } from '@apollo/client';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { FeatureGateAudience } from '../../__generated__/globalTypes';
import Button from '../../components/Button';
import Header from '../../components/Header';
import { useToast } from '../../components/Toast';
import { PATHS } from '../../paths';

import FeatureGateForm, { type IFeatureGateFormErrors, type IFeatureGateFormValues } from './FeatureGateForm';
import { findUserListOverlap, formStateToRule } from './ruleSerialization';

import type {
  AdminCreateFeatureGateMutation,
  AdminCreateFeatureGateMutationVariables,
} from './__generated__/NewFeatureGateScene.gql';

const ADMIN_CREATE_FEATURE_GATE = gql`
  mutation AdminCreateFeatureGate($input: CreateFeatureGateInput!) {
    adminCreateFeatureGate(input: $input) {
      id
      name
    }
  }
`;

// Lowercase ascii letters, digits, hyphens. Mirrors the canonicalization
// regex in `@orn/core/controllers/FeatureGateController` (`[a-z0-9-]+`)
// so the client surfaces the same error before round-tripping the
// mutation. The server still validates as defense in depth.
const GATE_NAME_RE = /^[a-z0-9-]+$/;

const INITIAL_FORM_VALUES: IFeatureGateFormValues = {
  name: '',
  description: '',
  // Default rollout: admins 100%, everyone 0%, gate enabled. Combined
  // with the SERVER_ONLY default audience, a gate created with these
  // values lights up for admin server-side checks immediately and stays
  // dark for everyone else until an operator dials `everyonePct` up.
  adminPct: 100,
  everyonePct: 0,
  allowUserIds: [],
  denyUserIds: [],
  isEnabled: true,
  audience: FeatureGateAudience.ServerOnly,
  defaultWhenError: false,
};

/**
 * Create-form scene. No prior state, so no edit-save confirmation: the
 * single submit creates the gate and returns the operator to the gate
 * list so the new gate is visible alongside its siblings. Surfaces a
 * public-name advisory on the name field because gate names are
 * returned by `enabledFeatureGates` to clients — embedding internal
 * codenames or PII would leak them across the wire.
 */
const NewFeatureGateScene = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [values, setValues] = React.useState<IFeatureGateFormValues>(INITIAL_FORM_VALUES);
  const [errors, setErrors] = React.useState<IFeatureGateFormErrors>({});

  const [createGate, { loading: isSubmitting }] = useMutation<
    AdminCreateFeatureGateMutation,
    AdminCreateFeatureGateMutationVariables
  >(ADMIN_CREATE_FEATURE_GATE);

  // Esc bails back to the list view — same convention as the edit scene.
  // The handler ignores Esc when typing in a form field so it does not
  // collide with native input behaviors (clearing autocomplete, etc.).
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      e.preventDefault();
      void navigate(PATHS.FEATURE_GATES);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const handleChange = (patch: Partial<IFeatureGateFormValues>) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors({});
  };

  const validate = (): IFeatureGateFormErrors | null => {
    const nextErrors: IFeatureGateFormErrors = {};
    const trimmedName = values.name.trim();
    if (trimmedName.length === 0) {
      nextErrors.name = 'Required.';
    } else if (!GATE_NAME_RE.test(trimmedName)) {
      nextErrors.name = 'Lowercase letters, digits, and hyphens only.';
    }
    const hasGrant = values.adminPct > 0 || values.everyonePct > 0 || values.allowUserIds.length > 0;
    if (!hasGrant) {
      nextErrors.rollout =
        values.denyUserIds.length > 0
          ? 'A deny-list only restricts an existing rollout. Set Everyone to 100% to allow all users except the deny-list, or add a rollout % / allow-list.'
          : 'Set a rollout %, add an allow-list, or leave the gate disabled to keep everyone out.';
    } else if (findUserListOverlap(values.allowUserIds, values.denyUserIds).length > 0) {
      nextErrors.rollout = 'A user can’t be on both the allow-list and the deny-list. Remove them from one.';
    }
    return Object.keys(nextErrors).length > 0 ? nextErrors : null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setErrors(v);
      return;
    }
    const ruleTree = formStateToRule(values.adminPct, values.everyonePct, values.allowUserIds, values.denyUserIds);
    if (ruleTree === null) {
      // Already covered by the no-grant error path above, but the type
      // narrowing requires the explicit guard.
      setErrors({ rollout: 'Set a rollout % or add an allow-list.' });
      return;
    }
    try {
      const result = await createGate({
        variables: {
          input: {
            name: values.name.trim(),
            description: values.description,
            ruleTree,
            audience: values.audience,
            defaultWhenError: values.defaultWhenError,
            isEnabled: values.isEnabled,
          },
        },
      });
      const created = result.data?.adminCreateFeatureGate;
      if (!created) {
        showToast({ type: 'error', message: 'Failed to create gate.' });
        return;
      }
      showToast({ type: 'success', message: `Created ${created.name}.` });
      void navigate(PATHS.FEATURE_GATES);
    } catch (err) {
      const message =
        err instanceof Error && err.message.includes('already exists')
          ? `A gate named ${values.name.trim()} already exists.`
          : 'Failed to create gate.';
      showToast({ type: 'error', message });
    }
  };

  return (
    <div className="bg-bg-primary text-fg-primary flex min-h-screen flex-col">
      <Header />
      <main className="flex flex-1 flex-col gap-5 px-6 py-5">
        <div className="flex items-baseline gap-3">
          <h1 className="text-sm font-semibold uppercase tracking-widest">New feature gate</h1>
          <button
            type="button"
            onClick={() => navigate(PATHS.FEATURE_GATES)}
            className="text-fg-tertiary hover:text-fg-primary focus-visible:outline-border-brand text-xs uppercase tracking-wider focus-visible:outline-2 focus-visible:-outline-offset-1"
          >
            ← Back to list
          </button>
        </div>

        {/* Public-name advisory — gate names are returned over the wire by
            `enabledFeatureGates` to any client whose audience matches, so
            anything embedded here is effectively public. The name is also
            immutable post-create (rename = delete + recreate per the
            controller comment in FeatureGateInputs.ts), so this is the
            one moment to get it right. */}
        <div className="border-fg-warning-primary bg-bg-warning-primary/10 flex flex-col gap-1 border-l-2 px-3 py-2">
          <span className="text-fg-warning-primary text-xs font-semibold uppercase tracking-wider">
            Heads up — gate names are public
          </span>
          <span className="text-fg-secondary text-xs leading-relaxed">
            The client bootstrap query <span className="font-mono">enabledFeatureGates</span> returns this name to every
            user the gate passes for. Treat it like an API field: no PII, no internal codenames, no shipping-window
            secrets. Names are also immutable — renaming means deleting and recreating the gate.
          </span>
        </div>

        {/* Cache-propagation advisory — `FeatureGateController` keeps a
            5-minute per-process cache (see TTL_MS in
            packages/core/src/controllers/FeatureGateController.ts). The
            writing process invalidates its own cache on success, but
            peer processes serve stale rows until their own TTL elapses,
            so cross-fleet propagation is bounded by 5 minutes. */}
        <div className="border-fg-warning-primary bg-bg-warning-primary/10 flex flex-col gap-1 border-l-2 px-3 py-2">
          <span className="text-fg-warning-primary text-xs font-semibold uppercase tracking-wider">
            Changes can take up to 5 minutes to propagate
          </span>
          <span className="text-fg-secondary text-xs leading-relaxed">
            App servers hold a 5-minute in-process cache of feature-gate state. After saving, allow up to 5 minutes for
            create, edit, disable, or delete actions to be live across every server.
          </span>
        </div>

        <FeatureGateForm values={values} errors={errors} onChange={handleChange} readOnlyNameAndAudience={false} />

        <div className="border-border-primary flex justify-end gap-2 border-t pt-4">
          <Button color="secondary" size="sm" onClick={() => navigate(PATHS.FEATURE_GATES)} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button color="primary" size="sm" onClick={() => void handleSubmit()} isLoading={isSubmitting}>
            Create gate
          </Button>
        </div>
      </main>
    </div>
  );
};

export default NewFeatureGateScene;
